/**
 * Represents an article card carousel.
 *
 * This class provides methods to manage the HTML content and DOM manipulation
 * for an article card carousel, including navigation buttons for previous and next actions.
 */
export default class ArticleCardCarousel {
  constructor(articleCardsHtml, carouselQuerySelector) {
    this.articleCardsHtml = articleCardsHtml;
    this.carouselQuerySelector = carouselQuerySelector;
  }

  /**
   * Generates the complete HTML for the carousel, including navigation buttons.
   *
   * @returns {string} The HTML string for the carousel, including the article cards and navigation buttons.
   *
   * @example
   * const carousel = new ArticleCardCarousel('<div>Card Content</div>', '[data-carousel="carousel1"]');
   * const html = carousel.getHtml();
   * console.log(html);
   */
  getHtml() {
    return (
      `${this.articleCardsHtml}
      <button aria-label="Previous" class="carousel-prev" data-left-arrow>
        <svg width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" clip-rule="evenodd"
            d="M7.647 13.02a.5.5 0 0 0 .707-.707L4.54 8.5h8.126a.5.5 0 1 0 0-1H4.54l3.813-3.813a.5.5 0 0 0-.707-.707L2.984 7.642a.503.503 0 0 0 0 .715l4.663 4.663Z"
            fill="#222" />
        </svg>
      </button>
      <button aria-label="Next" class="carousel-next" data-right-arrow>
        <svg width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" clip-rule="evenodd"
            d="M8.353 13.02a.5.5 0 1 1-.707-.707L11.46 8.5H3.332a.5.5 0 0 1 0-1h8.127L7.646 3.687a.5.5 0 0 1 .707-.707l4.663 4.662a.498.498 0 0 1 0 .715L8.354 13.02Z"
            fill="#222" />
        </svg>
      </button>`
    );
  }

  /**
   * Appends the carousel HTML to the DOM element specified by the query selector.
   *
   * This method retrieves the carousel element using the query selector and appends
   * the generated HTML content to it.
   *
   * @returns {void}
   *
   * @example
   * const carousel = new ArticleCardCarousel('<div>Card Content</div>', '[data-carousel="carousel1"]');
   * carousel.setHtml();
   */
  setHtml() {
    const carouselElement = this.getNode();
    const carouselHtml = this.getHtml();

    if (carouselElement) {
      const fragment = document.createRange().createContextualFragment(carouselHtml);
      carouselElement.appendChild(fragment);
    }
  }

  /**
   * Retrieves the DOM element for the carousel using the query selector.
   *
   * @returns {HTMLElement|null} The DOM element for the carousel, or null if not found.
   *
   * @example
   * const carousel = new ArticleCardCarousel('<div>Card Content</div>', '[data-carousel="carousel1"]');
   * const element = carousel.getNode();
   * console.log(element); // <div data-carousel="carousel1"></div>
   */
  getNode() {
    return document.querySelector(this.carouselQuerySelector);
  }
}